<script setup lang="ts">
import Button from "@/components/Reusables/Buttons/Button.vue";
import type { TooltipPlacement } from "@/stores/useTooltip";

import { useRevisionReadOnly, type RevisionReadOnly } from "../../composables/useRevisionReadOnly.composable";

/**
 * A `Button` that goes inert — and explains itself — while a frozen revision is previewed.
 *
 * Every write-committing button on a revision-gated surface wears the same four attributes:
 * the read-only tooltip, `color="disabled"`, `aria-disabled`, and a *suppressed* native
 * `disabled` (Chrome fires no mouse events on a natively disabled control, so the tooltip
 * explaining the block would never appear — the click is refused by the handler's
 * `blockedByPreview()` guard instead). This component is that pattern, written once.
 *
 * `color` and `disabled` describe the button as if the revision allowed writing — pass exactly
 * what a plain `Button` would get, validity folding included. The preview overrides both.
 * Everything else (`size`, `plus`, `data-cy`, `class`, `@click`, …) falls through to `Button`.
 *
 * The handler still guards: this component only styles and explains the block, it does not
 * swallow the click.
 *
 * `guard` names WHICH read-only rule governs the button. It defaults to `useRevisionReadOnly`, so
 * every existing caller is unchanged; a surface that blocks on more than the preview passes its own
 * `RevisionReadOnly`-shaped composable instead (waste sorting passes `useProjectEditReadOnly`, which
 * adds "you have Viewer access", RTLCA-5542). The composable is called here rather than handed in
 * already-invoked so it still bakes in THIS button's `placement`.
 */

defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    label: string;
    /** The color the button wears when writing is allowed. */
    color?: "primary" | "button" | "destructive" | "disabled";
    /** Validity/busy disabled — suppressed while blocked so the tooltip can still fire. */
    disabled?: boolean;
    isLoading?: boolean;
    /** Where the read-only tooltip sits relative to the button. Read once, at mount. */
    placement?: TooltipPlacement;
    /** The write guard to obey. Defaults to the preview-only one. Read once, at mount. */
    guard?: (placement: TooltipPlacement) => RevisionReadOnly;
  }>(),
  { color: "primary", disabled: false, isLoading: false, placement: "top" }
);

const { isReadOnly, readOnlyTooltip } = (props.guard ?? useRevisionReadOnly)(props.placement);
</script>

<template>
  <Button
    v-tooltip="readOnlyTooltip"
    v-bind="$attrs"
    :label="label"
    :color="isReadOnly ? 'disabled' : color"
    :disabled="!isReadOnly && disabled"
    :is-loading="isLoading"
    :aria-disabled="isReadOnly || undefined" />
</template>
